import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './paginator.scss';
import clsx from 'clsx';
import { ArrowLeft, ArrowRight } from 'react-feather';
import useTranslation from '../../../utils/translation-utils';
import { getArrayFromNToM } from '../../../utils/shape-utils';
import ButtonGroup from '../button-group';
import Icon, { IconColors, IconSizes } from '../icon';
import Button, { ButtonTypes } from '../button';
import { NEXT_BUTTON_ID, PREV_BUTTON_ID } from './constants';

const PaginatorVariants = {
  DEFAULT_S: 'variant-def-s',
  DEFAULT: 'variant-def',
  DEFAULT_L: 'variant-def-l',
  MINIMAL: 'variant-min',
  TINY: 'variant-tin', // absent as of now
  MOBILE: 'variant-mob',
};

const PaginatorAlignments = {
  LEFT: 'alignment-l',
  CENTER: 'alignment-c',
  RIGHT: 'alignment-r',
};

// TODO: This component needs documentation/comments and refactoring,
// It's complicated and hard to understand.

const PaginatorItemCounts = {
  // refers to the number of page buttons excluding the prev and next buttons
  [PaginatorVariants.DEFAULT]: 7,
  [PaginatorVariants.DEFAULT_S]: 5,
  [PaginatorVariants.DEFAULT_L]: 9,
};

const Paginator = ({
  pageCount, page, variant, alignment, onChange, className, disabled,
}) => {
  const [getTranslation] = useTranslation();
  const classNames = clsx('paginator-cont', className, alignment, variant);
  const [math, setMath] = useState({});

  useEffect(() => {
    const itemCount = PaginatorItemCounts[variant];
    if (itemCount) {
      if (itemCount >= pageCount) {
        setMath({
          isSplit: false,
          itemCount: pageCount,
        });
      } else {
        const triShare = (itemCount - 2) / 3;
        const duoShare = (itemCount - 1) / 2;
        const duoFit = page <= duoShare || (page > (pageCount - duoShare));
        let centerSize = 0;
        let centerStart = 0;
        let centerEnd = 0;
        let sideSize = 0;
        let skewedLeft = false;
        let skewedRight = false;

        if (!duoFit) {
          centerSize = itemCount - (Math.floor(triShare) * 2) - 2;
          sideSize = Math.floor(triShare);
          centerStart = page - Math.floor(centerSize / 2);
          centerEnd = page + Math.floor(centerSize / 2);
          skewedLeft = centerStart === sideSize + 2;
          skewedRight = centerEnd === (pageCount - sideSize - 1);
        } else {
          sideSize = (itemCount - 1) / 2;
        }

        setMath({
          isSplit: true,
          itemCount,
          duoFit,
          centerSize,
          sideSize,
          centerStart,
          centerEnd,
          skewedLeft,
          skewedRight,
        });
      }
    } else {
      setMath({});
    }
  }, [variant, page, pageCount]);

  const handlePageClicked = (pageNumber) => {
    if (onChange && typeof onChange === 'function' && pageNumber > 0 && pageNumber <= pageCount) {
      onChange.call(null, pageNumber);
    }
  };

  const handleNextClicked = () => {
    handlePageClicked(page + 1);
  };

  const handlePrevClicked = () => {
    handlePageClicked(page - 1);
  };

  const handleButtonClicked = (id) => {
    if (id === PREV_BUTTON_ID) {
      handlePrevClicked();
    } else if (id === NEXT_BUTTON_ID) {
      handleNextClicked();
    } else {
      handlePageClicked(id);
    }
  };

  const getPageNumberArray = () => {
    const {
      isSplit, duoFit, sideSize, centerStart, centerEnd, skewedLeft, skewedRight,
    } = math;
    let res = [];

    if (isSplit) {
      if (duoFit) {
        res = [
          ...getArrayFromNToM(1, sideSize),
          null,
          ...getArrayFromNToM(pageCount - sideSize + 1, pageCount),
        ];
      } else if (skewedLeft) {
        res = [
          ...getArrayFromNToM(1, centerEnd),
          null,
          ...getArrayFromNToM(pageCount - sideSize + 1, pageCount),
        ];
      } else if (skewedRight) {
        res = [
          ...getArrayFromNToM(1, sideSize),
          null,
          ...getArrayFromNToM(centerStart, pageCount),
        ];
      } else {
        res = [
          ...getArrayFromNToM(1, sideSize),
          null,
          ...getArrayFromNToM(centerStart, centerEnd),
          null,
          ...getArrayFromNToM(pageCount - sideSize + 1, pageCount),
        ];
      }
      return res;
    }
    return getArrayFromNToM(1, pageCount);
  };

  const getItemsButtonGroupProps = () => {
    const pageNumArray = getPageNumberArray();
    let res = [];

    for (let i = 0; i < pageNumArray.length; i += 1) {
      const pageNum = pageNumArray[i];
      if (pageNum) {
        res = [...res, {
          id: pageNum, label: pageNum, selected: pageNum === page, disabled,
        }];
      } else if (pageNum === null) {
        res = [...res, {
          id: `null${i}`, label: '...', selected: pageNum === page, disabled,
        }];
      }
    }

    return res;
  };

  const getMinimalPaginator = () => (
    <>
      <Button
        label="Previous"
        className="paginator-btn-prev"
        onClick={() => {
          handleButtonClicked(PREV_BUTTON_ID);
        }}
        type={ButtonTypes.SECONDARY}
      />
      <div className="paginator-page-text">
        {getTranslation('PAGE')}
        <span className="paginator-page-text-bold">
          {' '}
          {page}
          {' '}
        </span>
        {getTranslation('OF')}
        <span className="paginator-page-text-bold">
          {' '}
          {pageCount}
        </span>
      </div>
      <Button
        label="NEXT"
        className="paginator-btn-next"
        onClick={() => {
          handleButtonClicked(NEXT_BUTTON_ID);
        }}
        type={ButtonTypes.SECONDARY}
      />
    </>
  );

  const getTinyPaginator = () => {

  };

  const getMobilePaginator = () => (
    <>
      <button
        type="button"
        className={clsx(
          'paginator-btn-prev',
        )}
        disabled={page === 1}
        onClick={() => {
          handleButtonClicked(PREV_BUTTON_ID);
        }}
      >
        <Icon
          icon={ArrowLeft}
          size={IconSizes.MEDIUM}
          color={IconColors.GREY}
        />
      </button>
      <div className="paginator-page-text">
        {getTranslation('PAGE')}
        <span className="paginator-page-text-bold">
          {' '}
          {page}
          {' '}
        </span>
        {getTranslation('OF')}
        <span className="paginator-page-text-bold">
          {' '}
          {pageCount}
        </span>
      </div>
      <button
        type="button"
        className={clsx(
          'paginator-btn-next',
        )}
        disabled={page === pageCount}
        onClick={() => {
          handleButtonClicked(NEXT_BUTTON_ID);
        }}
      >
        <Icon
          icon={ArrowRight}
          size={IconSizes.MEDIUM}
          color={IconColors.GREY}
        />
      </button>
    </>
  );

  const getDefaultPaginator = () => {
    const pageItems = getItemsButtonGroupProps();
    const prevItem = {
      id: PREV_BUTTON_ID,
      label: 'Previous',
      renderLeftIcon:
      ({ color, size }) => <Icon icon={ArrowLeft} color={color} size={size} />,
      disabled: page === 1 || disabled,
    };
    const nextItem = {
      id: NEXT_BUTTON_ID,
      label: 'Next',
      renderRightIcon:
      ({ color, size }) => <Icon icon={ArrowRight} color={color} size={size} />,
      disabled: page === pageCount || pageCount === 0 || disabled,
    };
    return (
      <ButtonGroup
        onClick={(id) => {
          handleButtonClicked(id);
        }}
        items={[prevItem, ...pageItems, nextItem]}
      />
    );
  };

  const getView = () => {
    switch (variant) {
      case PaginatorVariants.TINY:
        return getTinyPaginator();
      case PaginatorVariants.MINIMAL:
        return getMinimalPaginator();
      case PaginatorVariants.DEFAULT:
      case PaginatorVariants.MOBILE:
        return getMobilePaginator();
      default:
        return getDefaultPaginator();
    }
  };

  return (
    <div className={classNames}>
      {getView()}
    </div>
  );
};

Paginator.propTypes = {
  pageCount: PropTypes.number,
  page: PropTypes.number,
  variant: PropTypes.oneOf(Object.values(PaginatorVariants)),
  onChange: PropTypes.func,
  className: PropTypes.string,
  alignment: PropTypes.oneOf(Object.values(PaginatorAlignments)),
  disabled: PropTypes.bool,
};

Paginator.defaultProps = {
  pageCount: 1,
  page: 1,
  variant: PaginatorVariants.DEFAULT_S,
  onChange: () => {},
  className: '',
  alignment: PaginatorAlignments.CENTER,
  disabled: false,
};

export {
  Paginator as default,
  PaginatorVariants,
  PaginatorAlignments,
};
